import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import styled from "styled-components";
import { LanguageToggle } from "../components/LanguageToggle";
import { useLanguage } from "../contexts/LanguageContext";
import { routes } from "../routes";
import StyledReactMarkdown from "../utils/StyledComponents/StyledReactMarkdown";

const PageContainer = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 32px 0;
`;

const ContentWrapper = styled.div`
    width: 60vw;
    max-width: 900px;
    min-width: 600px;
`;

const HeaderContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 32px;
    width: 100%;
`;

const LegalPage: React.FC = () => {
  const location = useLocation();
  const { language } = useLanguage();
  const [markdown, setMarkdown] = useState("");
  const [loading, setLoading] = useState(true);

  // Determine page name from route
  const getPageName = useCallback(() => {
    if (location.pathname === routes.imprint) {
      return "imprint";
    } else if (location.pathname === routes.privacyPolicy) {
      return "privacy-policy";
    }
    return "imprint"; // default
  }, [location.pathname]);

  useEffect(() => {
    setLoading(true);
    const pageName = getPageName();
    const markdownPath = `/md/${pageName}_${language}.md`;

    fetch(markdownPath)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to load ${markdownPath}`);
        }
        return response.text();
      })
      .then((text) => {
        setMarkdown(text);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error loading markdown:", error);
        setMarkdown(
          `# Error\n\nFailed to load content for ${getPageName()} in ${language}.`,
        );
        setLoading(false);
      });
  }, [language, getPageName]);

  if (loading) {
    return (
      <PageContainer>
        <ContentWrapper>
          <p>Loading...</p>
        </ContentWrapper>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <ContentWrapper>
        <HeaderContainer>
          <div></div>
          <LanguageToggle variant="light" />
        </HeaderContainer>
        <StyledReactMarkdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[rehypeRaw]}
        >
          {markdown}
        </StyledReactMarkdown>
      </ContentWrapper>
    </PageContainer>
  );
};

export default LegalPage;
